<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import AlertsIndicator from '@/components/AlertsIndicator.vue';
import DarkModeToggle from '@/components/DarkModeToggle.vue';
import DocsLinkHeader from '@/components/DocsLinkHeader.vue';
import KastenLogo from '@/components/KastenLogo.vue';
import KLink from '@/components/KLink.vue';
import KSearch from '@/components/KSearch.vue';
import UserMenu from '@/components/UserMenu.vue';
import { useMainStore } from '@/stores/main';
import IconMenu from '~icons/lucide/menu';

const emit = defineEmits<{ openSidebar: [] }>();

const route = useRoute();
const mainStore = useMainStore();

function skipToMain() {
  const mainElement = document.getElementById('main-content');
  if (mainElement) {
    mainElement.scrollIntoView({ behavior: 'smooth' });
    mainElement.focus();
  }
}

const isMCPage = computed(() => !!route.meta?.isMC);
const isLargeScreen = useMediaQuery('(min-width: 1024px)');
</script>

<template>
  <header
    class="mb-8 flex h-16 items-center border-b border-black/10 px-4 pr-2 dark:border-navy-600 sm:px-6"
    :class="{ 'print:hidden': route.name === 'ReportDetails' }"
  >
    <a
      href="#main-content"
      class="text-primary-content absolute left-0 z-[999] m-3 -translate-y-16 bg-white p-3 transition focus:translate-y-0 focus-visible:outline-2 focus-visible:outline-blue-400 dark:bg-navy-800 dark:text-white"
      @click.prevent="skipToMain"
    >
      Skip to main content
    </a>
    <div class="flex h-full w-full justify-between gap-1 sm:gap-4">
      <div class="flex items-center gap-3 lg:hidden">
        <button
          type="button"
          class="-m-2.5 p-2.5 text-gray-700 focus-visible:outline-2 focus-visible:outline-blue-400 dark:text-gray-400"
          @click="emit('openSidebar')"
          id="mobileMenuButton"
        >
          <span class="sr-only">Open sidebar</span>
          <IconMenu class="h-8 w-8" aria-hidden="true" />
        </button>
        <KLink
          class="inline-flex h-16 shrink-0 items-center text-gray-700 focus-visible:outline-2 focus-visible:outline-blue-400 dark:text-gray-400"
          :to="isMCPage ? { name: 'MCClusters' } : { name: 'Dashboard' }"
        >
          <span class="sr-only">Veeam Kasten logo</span>
          <KastenLogo class="h-10" orient="vertical" aria-hidden="true" />
        </KLink>
      </div>

      <div v-if="!isMCPage && isLargeScreen" class="flex flex-grow items-center">
        <KSearch />
      </div>

      <div
        class="ml-auto flex h-full items-center justify-end gap-2 justify-self-end text-sm sm:gap-4"
      >
        <DocsLinkHeader />

        <div class="h-10 border-r border-gray-300 dark:border-navy-600"></div>

        <UserMenu />

        <AlertsIndicator
          :alertMessages="mainStore.alertMessages"
          class="h-10 bg-transparent p-2 text-gray-700 shadow-none ring-0 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-offset-0 dark:bg-transparent dark:text-gray-400 dark:ring-0 dark:hover:bg-navy-800 dark:hover:text-gray-50"
        />

        <DarkModeToggle />
      </div>
    </div>
  </header>
</template>
